import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Play, Pause, Download, ShoppingCart } from "lucide-react";
import BizumModal from "@/components/BizumModal";
import { usePlayer } from "@/context/PlayerContext";
import { coverUrl, downloadTrackUrl, fmtDuration, queueAfter, buyItemFor, detailPathFor, sectionOf, SECTION_LABELS } from "@/lib/api";
import { Pager, usePager } from "@/components/Pager";
import { useAccessCode } from "@/context/AccessCodeContext";
import { useAuth } from "@/context/AuthContext";
import { ShareButton, shareUrlFor, storyUrlFor } from "@/components/ShareButton";

const pill = "font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border transition-colors shrink-0";

export function CompactTrackList({ tracks, perPage = 10 }) {
  const p = usePlayer();
  const pg = usePager(tracks, perPage);
  const ac = useAccessCode();
  const auth = useAuth();
  const [buying, setBuying] = useState(null);
  return (
    <>
    <div className="divide-y divide-white/5 border border-white/5 rounded-xl overflow-hidden bg-[#111116]/60" data-testid="compact-track-list">
      {pg.slice.map((t) => {
        const isCur = p?.current?.id === t.id;
        const playing = isCur && p?.isPlaying;
        return (
          <div key={t.id} data-testid={`compact-row-${t.id}`} onMouseEnter={() => p.prefetch?.(t)} onClick={() => (isCur ? p.togglePlay() : p.playTrack(t, queueAfter(tracks, t.id)))}
            className={`flex items-center gap-2 sm:gap-3 px-2.5 sm:px-3 py-2 cursor-pointer select-none transition-colors ${isCur ? "bg-red-950/20" : "hover:bg-white/[0.03]"}`}>
            <div className="relative w-9 h-9 sm:w-10 sm:h-10 rounded overflow-hidden bg-neutral-900 shrink-0">
              {t.cover_path && <img src={coverUrl(t.cover_path, 160)} alt="" className="w-full h-full object-cover" />}
              <span className="absolute inset-0 flex items-center justify-center bg-black/40">
                {playing ? <Pause className="w-3.5 h-3.5 text-white" /> : <Play className="w-3.5 h-3.5 text-white ml-0.5" />}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <Link to={detailPathFor(t)} onClick={(e) => e.stopPropagation()} data-testid={`compact-title-${t.id}`}
                className={`block text-[13px] sm:text-sm leading-snug break-words hover:text-red-400 ${isCur ? "text-red-400" : "text-white/85"}`}>{t.title}</Link>
              <div className="text-[11px] sm:text-xs text-white/50 truncate flex items-center gap-2">
                <span className="truncate">{t.artist}</span>
                {(t.versions || []).length > 0 && <Link to={detailPathFor(t)} onClick={(e) => e.stopPropagation()} data-testid={`compact-versions-${t.id}`} className="text-pink-400 hover:text-pink-300 shrink-0 font-semibold">{t.versions.length + 1} versiones</Link>}
              </div>
              <div className="flex sm:hidden items-center gap-1.5 mt-1">
                <span className="font-mono text-[9px] text-white/40">{fmtDuration(t.duration)}</span>
                {t.pack_id && <Link to={`/pack/${t.pack_id}`} onClick={(e) => e.stopPropagation()} className={`${pill} text-sky-400 border-sky-800/60 bg-sky-950/40`}>Pack</Link>}
                <Link to={detailPathFor(t)} onClick={(e) => e.stopPropagation()} className={`${pill} text-emerald-400 border-emerald-800/60 bg-emerald-950/40`}>Ver tema</Link>
                <ShareButton url={shareUrlFor(t)} storyUrl={storyUrlFor(t)} title={t.title} testid={`compact-share-m-${t.id}`} className="p-1" size="w-3 h-3" />
              </div>
            </div>
            <div className="hidden sm:flex items-center gap-2 shrink-0">
              <Link to={`/${{ remix: "remixes", mashup: "mashups", original: "pool", pool: "pool" }[sectionOf(t)]}`} onClick={(e) => e.stopPropagation()} data-testid={`compact-section-${t.id}`} title="Ir a la sección"
                className={`${pill} text-sky-400 border-sky-800/60 bg-sky-950/40 hover:bg-sky-900/50 hover:text-white`}>{SECTION_LABELS[sectionOf(t)]}</Link>
              {t.pack_id && (
                <Link to={`/pack/${t.pack_id}`} onClick={(e) => e.stopPropagation()} data-testid={`compact-pack-link-${t.id}`} title={t.pack_title ? `Ver pack: ${t.pack_title}` : "Ver pack"}
                  className={`${pill} text-sky-400 border-sky-800/60 bg-sky-950/40 hover:bg-sky-900/50 hover:text-white`}>Pack</Link>
              )}
              <Link to={detailPathFor(t)} onClick={(e) => e.stopPropagation()} data-testid={`compact-view-${t.id}`} title="Ver tema"
                className={`${pill} text-emerald-400 border-emerald-800/60 bg-emerald-950/40 hover:bg-emerald-900/50 hover:text-white`}>Ver tema</Link>
              <span className="font-mono text-[10px] text-white/40 w-9 text-right">{fmtDuration(t.duration)}</span>
              <ShareButton url={shareUrlFor(t)} storyUrl={storyUrlFor(t)} title={t.title} testid={`compact-share-${t.id}`} className="p-1.5" size="w-3.5 h-3.5" />
            </div>
            {t.price === 0 || ac.allows(t.is_pista ? "pack" : "track", t.is_pista ? t.pack_id : t.id) ? (
              <div className="flex items-center gap-1 shrink-0">
                <span className={`${pill} text-emerald-400 border-emerald-800/50 bg-emerald-950/40`} data-testid={`compact-price-${t.id}`}>{t.price === 0 ? "Free" : "VIP"}</span>
                <a href={ac.withCode(t.download_url || downloadTrackUrl(t.id))} onClick={(e) => { e.stopPropagation(); auth.requireAuth(e); }} data-testid={`compact-download-${t.id}`} title="Descargar" className="p-1.5 text-white/50 hover:text-red-500"><Download className="w-4 h-4 sm:w-3.5 sm:h-3.5" /></a>
              </div>
            ) : (
              <div className="flex items-center gap-1 shrink-0">
                <span className={`${pill} text-red-400 border-red-800/50 bg-red-950/40`} data-testid={`compact-price-${t.id}`}>€{Number(t.price).toFixed(2)}</span>
                <button type="button" onClick={(e) => { e.stopPropagation(); setBuying(t); }} data-testid={`compact-buy-${t.id}`} title="Comprar y descargar" className="p-1.5 text-red-400 hover:text-white hover:bg-red-600/20 rounded"><ShoppingCart className="w-4 h-4 sm:w-3.5 sm:h-3.5" /></button>
              </div>
            )}
          </div>
        );
      })}
    </div>
    <Pager {...pg} testid="compact-track-list-pager" />
    {buying && <BizumModal open={!!buying} onClose={() => setBuying(null)} {...buyItemFor(buying)} />}
    </>
  );
}
